import { RadioGroupItem } from "@/ui/components/RadioGroup";
import { cn } from "@/ui/lib/cn";

/**
 * Card-style radio. Composed on top of shadcn's RadioGroup so a parent
 * <RadioGroup value=… onValueChange=…> drives selection. The whole card
 * is the click target; the small radio dot lives in the corner.
 *
 *   <RadioGroup value={mode} onValueChange={setMode}>
 *     <div className="grid gap-3 sm:grid-cols-2">
 *       <RadioCard value="derived" title="Derived from shop rate × markup"
 *                  help="TrueCost calculates bill rate automatically." />
 *       <RadioCard value="independent" title="Set independently"
 *                  help="Enter a flat bill rate not tied to shop rate or markup." />
 *     </div>
 *   </RadioGroup>
 */
export interface RadioCardProps {
  value: string;
  title: React.ReactNode;
  help?: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

export function RadioCard({ value, title, help, disabled, className }: RadioCardProps) {
  const id = `radio-card-${value}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        "group relative flex cursor-pointer flex-col gap-1 rounded-lg border bg-card p-4 transition-all",
        "hover:border-slate-500",
        "has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:shadow-glow",
        "has-[[data-state=checked]]:ring-1 has-[[data-state=checked]]:ring-primary",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <RadioGroupItem id={id} value={value} disabled={disabled} className="mt-1" />
        <div className="flex flex-col gap-1">
          <span className="font-medium text-foreground">{title}</span>
          {help && <span className="text-sm text-muted-foreground">{help}</span>}
        </div>
      </div>
    </label>
  );
}
